import { $pegApi } from '@/boot/peg-api'
import { $rollbar } from '@/boot/rollbar'
import { DEFAULT_PHOTO } from '@/constants/appReturn.const'

/**
 * Customer Return Wrong Item Store
 *
 * Manages wrong item identification and product search during customer return flow.
 * This store handles:
 * - Product search by SKU or barcode
 * - Image capture for wrong items
 * - Product selection and confirmation
 * - Multi-step search workflow (input → searching → results)
 *
 * @public API:
 * - State: capturedImage, enterManually, processingImage, searchResults, searchSku, selectedProduct, selectedProductId, selectedWrongItemsById, step
 * - Actions: clearForItem, confirmProduct, continueWithoutMatch, handleSearch, searchAgain, selectProduct, toggleInputMethod
 *
 * @store customerReturnWrongItem
 */

// ============================================================================
// STORE DEFINITION
// ============================================================================

export const useCustomerReturnWrongItemStore = defineStore('customerReturnWrongItem', () => {
  const customerReturnStore = useCustomerReturnStore()

  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Captured image of the wrong item
   * @type {Ref<Object>}
   * @public
   */
  const capturedImage = ref({ ...DEFAULT_PHOTO })

  /**
   * Whether user is entering SKU manually vs scanning
   * @type {Ref<boolean>}
   * @public
   */
  const enterManually = ref(false)

  /**
   * Whether image is being processed
   * @type {Ref<boolean>}
   * @public
   */
  const processingImage = ref(false)

  /**
   * Search results from product lookup
   * @type {Ref<Array|null>}
   * @public
   */
  const searchResults = ref(null)

  /**
   * SKU being searched
   * @type {Ref<string|null>}
   * @public
   */
  const searchSku = ref(null)

  /**
   * Currently selected product from search
   * @type {Ref<Object|null>}
   * @public
   */
  const selectedProduct = ref(null)

  /**
   * Store selected wrong items by item id for easy access
   * @type {Ref<Object>}
   * @public
   */
  const selectedWrongItemsById = ref({})

  /**
   * Current step in the workflow: input | searching | results
   * @type {Ref<string>}
   * @public
   */
  const step = ref('input')

  // ============================================================================
  // COMPUTED
  // ============================================================================

  /**
   * ID of the currently selected product
   * @type {ComputedRef<string|null>}
   * @public
   */
  const selectedProductId = computed(() => selectedProduct.value?.id || null)

  // ============================================================================
  // ACTIONS (PUBLIC API)
  // ============================================================================

  /**
   * Confirm the selected product and captured image for the wrong item
   * @public
   */
  const confirmProduct = () => {
    const itemId = customerReturnStore?.currentItem?.id
    if (!itemId) {
      throw new Error('No current item to confirm wrong item for.')
    }
    selectedWrongItemsById.value[itemId] = {
      selectedProduct: { ...selectedProduct.value },
      capturedImage: { ...capturedImage.value },
      verificationSkipped: false,
      skippedReason: null,
    }
  }

  /**
   * Continue without selecting a product match
   * @public
   */
  const continueWithoutMatch = () => {
    const itemId = customerReturnStore?.currentItem?.id
    if (!itemId) {
      throw new Error('No current item to continue without match for.')
    }
    selectedWrongItemsById.value[itemId] = {
      selectedProduct: null,
      capturedImage: null,
      verificationSkipped: true,
      skippedReason: 'user-skipped',
    }
  }

  /**
   * Search for products by SKU - looks up in retailer for a match against sku and barcode fields
   * @param {string} retailerId - Retailer ID to search within
   * @param {string} sku - SKU or barcode to search for
   * @returns {Promise<void>}
   * @public
   */
  const handleSearch = async (retailerId, sku) => {
    searchSku.value = sku
    step.value = 'searching'

    let variants
    try {
      ;({ variants } = await $pegApi.Retailers.productLookup(retailerId, sku))
    } catch (error) {
      // $pegApi now throws on error; without this catch a failed lookup leaves
      // the UI stuck in 'searching' with an unhandled rejection. Advance to an
      // empty results list (prior { success:false } behavior).
      $rollbar.error('Wrong-item product lookup failed', error)
      step.value = 'results'
      searchResults.value = []
      return
    }

    step.value = 'results'
    if (!Array.isArray(variants)) {
      searchResults.value = []
      return
    }
    searchResults.value = variants.map(variant => {
      const images = variant?.product?.media
        ? variant.product.media.map((img, index) => ({
            id: img.preview.image.id,
            alt: `${variant.product?.title} Image ${index + 1}`,
            label: `Image ${index + 1}`,
            url: img.preview.image.url,
          }))
        : []

      const product = variant?.product || {}

      return {
        ...variant,
        title: product?.title || 'Untitled Product',
        description: product?.description,
        image: images.length ? images[0] : null,
        images,
      }
    })

    if (searchResults.value.length === 1) {
      selectedProduct.value = searchResults.value[0]
    }

    return
  }

  /**
   * Reset the search state to allow searching again
   * @public
   */
  const searchAgain = () => {
    step.value = 'input'
    searchResults.value = null
    searchSku.value = null
    capturedImage.value = { ...DEFAULT_PHOTO }
    selectedProduct.value = null
  }

  /**
   * Select a product from the search results
   * @param {Object} product - Product object to select
   * @public
   */
  const selectProduct = product => {
    selectedProduct.value = product
    if (selectedProductId.value === product.id) {
      return
    }
  }

  /**
   * Toggle between manual SKU entry and photo capture input methods
   * @public
   */
  const toggleInputMethod = () => {
    enterManually.value = !enterManually.value
    searchSku.value = null
    capturedImage.value = { ...DEFAULT_PHOTO }
  }

  /**
   * Clear wrong item data for a specific item and reset transient search state.
   * Used when a customer confirms they received the correct item during product verification,
   * ensuring stale wrong item data doesn't persist.
   * @param {string} itemId - The item ID to clear wrong item data for
   * @public
   */
  const clearForItem = itemId => {
    if (!itemId) return
    delete selectedWrongItemsById.value[itemId]
    // Also reset transient search state so it's fresh if they revisit
    capturedImage.value = { ...DEFAULT_PHOTO }
    enterManually.value = false
    processingImage.value = false
    searchResults.value = null
    searchSku.value = null
    selectedProduct.value = null
    step.value = 'input'
  }

  /**
   * Reset the store to initial state
   * @public
   */
  const reset = () => {
    capturedImage.value = { ...DEFAULT_PHOTO }
    enterManually.value = false
    processingImage.value = false
    searchResults.value = null
    searchSku.value = null
    selectedProduct.value = null
    selectedWrongItemsById.value = {}
    step.value = 'input'
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    // State
    capturedImage,
    enterManually,
    processingImage,
    searchResults,
    searchSku,
    selectedProduct,
    selectedWrongItemsById,
    step,
    // Computed
    selectedProductId,
    // Actions
    clearForItem,
    confirmProduct,
    continueWithoutMatch,
    handleSearch,
    reset,
    searchAgain,
    selectProduct,
    toggleInputMethod,
  }
})
